import type { Gender } from '$/components/elements/Questions/lists/genderList';
import type { TQuestionAnswer } from '$/types/components/questions';

import endOfMonth from 'date-fns/endOfMonth';
import * as yup from 'yup';

import { yupCheckboxInputQuestion } from '$/components/elements/Questions/CheckboxInputQuestion';
import { yupDateInputQuestion } from '$/components/elements/Questions/DateInputQuestion';
import { yupEmailInputQuestion } from '$/components/elements/Questions/EmailnputQuestion';
import { yupSliderInputQuestion } from '$/components/elements/Questions/SliderInputQuestion';
import { yupTextInputQuestion } from '$/components/elements/Questions/TextInputQuestion';
import ErrorMessages from '$/types/enums/ErrorMessages';
import TestTypes from '$/types/enums/TestTypes';
import autocompleteSchema from '$/yup/schemas/autocompleteSchema';
import selectAnswerSchema from '$/yup/schemas/selectAnswerSchema';
import dateAfterStartOfAnotherDate from '$/yup/tests/dateAfterStartOfAnotherDate';
import dateNotInThePast from '$/yup/tests/dateNotInThePast';
import dateOlderThanTest from '$/yup/tests/dateOlderThanTest';

export type MajorSchemaType = {
  major?: TQuestionAnswer[];
};

export type FieldSchemaType = {
  field?: TQuestionAnswer[];
};

export type CitySchemaType = {
  city?: TQuestionAnswer[];
};

export type UniversitySchemaType = {
  university?: TQuestionAnswer[];
};

export type ExpectedDegreeSchemaType = {
  expectedDegree?: TQuestionAnswer[];
};

export type StudiesSchemaType = {
  startDate?: Date;
  endDate?: Date;
  variant?: TQuestionAnswer[];
};

export type PreviousEducationSchemaType = {
  previousEducation?: TQuestionAnswer[];
  schoolNote?: number;
};

export type EnglishLevelSchemaType = {
  englishLevel?: TQuestionAnswer[];
};

export type SignUpSchemaType = {
  gender?: Gender;
  firstName?: string;
  lastName?: string;
  birthDate?: Date;
  email?: string;
};

export type RegistrationSchemaType = MajorSchemaType &
  FieldSchemaType &
  CitySchemaType &
  UniversitySchemaType &
  ExpectedDegreeSchemaType &
  StudiesSchemaType &
  PreviousEducationSchemaType &
  EnglishLevelSchemaType &
  SignUpSchemaType;

export const majorSchema = yup.object({
  major: autocompleteSchema.required(),
});

export const fieldSchema = yup.object({
  field: autocompleteSchema.required(),
});

export const citySchema = yup.object({
  city: autocompleteSchema.required(),
});

export const universitySchema = yup.object({
  university: autocompleteSchema.required(),
});

export const expectedDegreeSchema = yup.object({
  expectedDegree: selectAnswerSchema.required(),
});

export const studiesSchema = yup
  .object({
    startDate: yupDateInputQuestion.required(),
    endDate: yupDateInputQuestion
      .test(
        TestTypes.DATE_IN_THE_PAST,
        ErrorMessages.DATE_IN_THE_PAST,
        dateNotInThePast
      )
      .test(
        TestTypes.DATE_SHOULD_BE_AFTER_START,
        ErrorMessages.DATE_SHOULD_BE_AFTER_START,
        dateAfterStartOfAnotherDate('startDate')
      )
      .transform((date: Date | null) => {
        if (!date) return null;
        return endOfMonth(date);
      })
      .required(),
    variant: selectAnswerSchema.required(),
  })
  .required();

export const previousEducationSchema = yup
  .object({
    previousEducation: selectAnswerSchema.required(),
    schoolNote: yupSliderInputQuestion.required(),
  })
  .required();

export const englishLevelSchema = yup
  .object({
    englishLevel: selectAnswerSchema.required(),
  })
  .required();

export const signUpSchema = yup
  .object({
    gender: yupTextInputQuestion.required(),
    firstName: yupTextInputQuestion.required(),
    lastName: yupTextInputQuestion.required(),
    birthDate: yupDateInputQuestion
      .test(
        TestTypes.AT_LEAST_18_YO,
        ErrorMessages.AT_LEAST_18_YO,
        dateOlderThanTest(568025136000)
      )
      .required(),
    email: yupEmailInputQuestion.required(),
    checkbox: yupCheckboxInputQuestion.required(),
  })
  .required();

const registrationSchema = yup
  .object()
  .concat(majorSchema)
  .concat(fieldSchema)
  .concat(citySchema)
  .concat(universitySchema)
  .concat(expectedDegreeSchema)
  .concat(previousEducationSchema)
  .concat(englishLevelSchema)
  .concat(signUpSchema);

export default registrationSchema;
